import { useEffect, useRef, useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/config/queryConfig'
import { useAuthStore } from '@/store/authStore'

interface WebSocketMessage {
  type: string
  event?: any
  message?: string
  timestamp?: string
}

interface UseWebSocketOptions {
  enabled?: boolean
  onMessage?: (message: WebSocketMessage) => void
  onError?: (error: Event) => void
}

function getWebSocketURL(): string {
  if (import.meta.env.VITE_WS_URL) {
    return import.meta.env.VITE_WS_URL
  }

  const apiUrl = import.meta.env.VITE_API_URL || window.location.origin + '/api'
  let wsUrl = apiUrl
    .replace(/^https:\/\//, 'wss://')
    .replace(/^http:\/\//, 'ws://')
    .replace(/\/api\/?$/, '')

  return `${wsUrl}/ws/notifications/`
}

const HEARTBEAT_INTERVAL = 30000
const BASE_RECONNECT_DELAY = 3000
const MAX_RECONNECT_DELAY = 30000

export function useWebSocket(options: UseWebSocketOptions = {}) {
  const { enabled = true, onMessage, onError } = options

  const wsRef = useRef<WebSocket | null>(null)
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null)
  const reconnectRef = useRef<NodeJS.Timeout | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const shouldConnectRef = useRef(enabled)

  // Keep latest callbacks in refs so connect() never captures a stale closure
  const onMessageRef = useRef(onMessage)
  const onErrorRef = useRef(onError)
  useEffect(() => { onMessageRef.current = onMessage }, [onMessage])
  useEffect(() => { onErrorRef.current = onError }, [onError])

  const [isConnected, setIsConnected] = useState(false)
  const [lastEvent, setLastEvent] = useState<any>(null)

  const queryClient = useQueryClient()

  const clearHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current)
      heartbeatRef.current = null
    }
  }, [])

  const clearReconnect = useCallback(() => {
    if (reconnectRef.current) {
      clearTimeout(reconnectRef.current)
      reconnectRef.current = null
    }
  }, [])

  const connect = useCallback(() => {
    if (!shouldConnectRef.current) return

    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
      return
    }

    const token = useAuthStore.getState().accessToken
    if (!token) {
      console.log('[WebSocket] No access token available, skipping connection')
      return
    }

    try {
      const wsUrl = getWebSocketURL()
      const wsUrlWithToken = `${wsUrl}?token=${encodeURIComponent(token)}`
      console.log('[WebSocket] Connecting to:', wsUrl)
      const ws = new WebSocket(wsUrlWithToken)

      ws.onopen = () => {
        console.log('[WebSocket] Connected')
        setIsConnected(true)
        reconnectAttemptsRef.current = 0
        clearReconnect()

        // Heartbeat to keep connection alive
        clearHeartbeat()
        heartbeatRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'ping', timestamp: new Date().toISOString() }))
          }
        }, HEARTBEAT_INTERVAL)
      }

      ws.onmessage = (event: MessageEvent) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data)

          if (message.type === 'pong') return

          if (message.type === 'connection_established') {
            console.log('[WebSocket] Connection established')
            reconnectAttemptsRef.current = 0
            return
          }

          if (message.type === 'notification' && message.event) {
            console.log('[WebSocket] New event notification:', message.event)
            setLastEvent(message.event)
            queryClient.invalidateQueries({ queryKey: queryKeys.events.recent() })
            // #078 addendum: invalidate the EventsPage infinite query so an open list
            // refreshes immediately on a new event (partial-prefix matches all filter variants).
            queryClient.invalidateQueries({ queryKey: ['events', 'paginated'] })
            queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unread() })
            queryClient.invalidateQueries({ queryKey: queryKeys.cameras.all() })
          }

          onMessageRef.current?.(message)
        } catch (error) {
          console.error('[WebSocket] Failed to parse message:', error)
        }
      }

      ws.onerror = (error) => {
        console.error('[WebSocket] Error:', error)
        try { onErrorRef.current?.(error) } catch { /* ignore handler errors */ }
      }

      ws.onclose = (event) => {
        console.log('[WebSocket] Disconnected:', event.code, event.reason)
        setIsConnected(false)
        clearHeartbeat()

        if (shouldConnectRef.current) {
          // Exponential backoff: 3s, 6s, 12s, 24s, 30s (capped), 30s, ...
          const delay = Math.min(
            BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current),
            MAX_RECONNECT_DELAY
          )
          reconnectAttemptsRef.current++
          console.log(`[WebSocket] Reconnecting in ${delay / 1000}s (attempt ${reconnectAttemptsRef.current})...`)
          clearReconnect()
          reconnectRef.current = setTimeout(connect, delay)
        }
      }

      wsRef.current = ws
    } catch (error) {
      console.error('[WebSocket] Connection failed:', error)
      setIsConnected(false)
    }
  }, [queryClient, clearHeartbeat, clearReconnect])

  const disconnect = useCallback(() => {
    shouldConnectRef.current = false
    clearHeartbeat()
    clearReconnect()

    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }

    setIsConnected(false)
  }, [clearHeartbeat, clearReconnect])

  useEffect(() => {
    shouldConnectRef.current = enabled

    if (enabled) {
      connect()
    } else {
      disconnect()
    }

    return () => {
      disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled])

  return {
    isConnected,
    lastEvent,
    reconnect: connect,
    disconnect,
  }
}
